/*
 * File: DriveOverviewDialog.tsx
 * Author: Austin Robinson
 * Date Created: 12/07/2025
 * Copyright (c) DrivingDoc 2025
 */
import { useState } from 'react';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import { DialogProps } from '@toolpad/core/useDialogs';

import { getCoursePeriodOptions } from '@drivingdoc/api-client/generated/react-query';
import type { CoursePeriod } from 'types/periods';

import {
  DialogHeader,
  DialogHeaderSkeleton,
  InstructorAvatars,
  InstructorAvatarsSkeleton,
  InstructorNotesSkeleton,
  StudentSection,
  StudentSectionSkeleton,
  StudentTabs,
} from './components';
import { DriveOverviewDialogContext } from './context';

export interface DriveOverviewDialogPayload {
  /**
   * The ID of the course period to display
   */
  periodId: string;

  /**
   * Optional student ID to auto-select a specific student's tab
   */
  studentId?: string;
}

function DriveOverviewDialogContent({ payload, open, onClose }: DialogProps<DriveOverviewDialogPayload>) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('lg'));

  const period = useQuery({
    ...getCoursePeriodOptions({
      path: {
        id: payload.periodId,
      },
    }),
    enabled: open,
  });

  const periodData = period.data as CoursePeriod | undefined;
  const enrollments = periodData?.period_enrollments ?? [];

  // Use student ID as the selected tab value
  const initialStudentId = payload.studentId ?? enrollments[0]?.student_user_id;
  const [selectedStudentId, setSelectedStudentId] = useState<string>(initialStudentId ?? '');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setSelectedStudentId(newValue);
  };

  // Find the current enrollment based on selected student ID
  const currentEnrollment = enrollments.find((enrollment) => enrollment.student_user_id === selectedStudentId);

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog fullScreen={fullScreen} fullWidth maxWidth="lg" open={open} onClose={handleClose}>
      <DriveOverviewDialogContext.Provider
        value={{
          onClose: handleClose,
          period: periodData ?? undefined,
        }}
      >
        {period.isLoading ? (
          <DialogHeaderSkeleton onClose={handleClose} />
        ) : (
          periodData && <DialogHeader />
        )}

        <DialogContent dividers>
          {period.isLoading ? (
            <Stack spacing={2}>
              {/* Instructor Avatars Skeleton */}
              <InstructorAvatarsSkeleton />

              {/* Student Section Skeleton */}
              <StudentSectionSkeleton />

              {/* Instructor Notes Skeleton */}
              <InstructorNotesSkeleton />
            </Stack>
          ) : (
            <Stack spacing={2}>
              {/* Instructor Avatars */}
              <InstructorAvatars instructors={periodData?.course_period_instructors ?? []} />

              {/* Show tabs when there are multiple enrollments */}
              {enrollments.length > 1 && (
                <StudentTabs
                  enrollments={enrollments}
                  selectedStudentId={selectedStudentId}
                  onTabChange={handleTabChange}
                />
              )}

              {/* Otherwise show divider when there are not multiple enrollments */}
              {enrollments.length <= 1 && <Divider />}

              {/* Render the selected student section */}
              <StudentSection enrollment={currentEnrollment} />
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            flexWrap: 'wrap',
            gap: 1,
            px: 2,
            py: 1.5,
          }}
        >
          {/* todo: add drive related actions here */}
          {/* <Box display="flex" gap={1} flexWrap="wrap" flex={1}>
            <Button
              size="small"
              startIcon={<EmailIcon />}
              variant="outlined"
              onClick={() => console.log('Send reminder')}
            >
              Send Reminder
            </Button>

            <Button
              size="small"
              startIcon={<PrintIcon />}
              variant="outlined"
              onClick={() => console.log('Print roster')}
            >
              Print Roster
            </Button>
          </Box> */}

          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </DriveOverviewDialogContext.Provider>
    </Dialog>
  );
}

export default function DriveOverviewDialog(props: DialogProps<DriveOverviewDialogPayload>) {
  const queryClient = useQueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <DriveOverviewDialogContent {...props} />
    </QueryClientProvider>
  );
}
